import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useQuery } from '@apollo/client';
import { useSelector, useStore } from 'react-redux';
import isEqual from 'lodash.isequal';
import { useTranslation } from 'react-i18next';
import { useTheme } from 'styled-components';
import { useAuth } from '../../../../hooks/useAuth';

//Styled Components
import {
  Container,
  ButtonContainer,
  PaginationButtonsStyled,
  MainButtonStyled,
  CloseButton,
} from './styles';

// Types
import { ModalProps } from './types';
import {
  dataTable,
  imperativeRefPagination,
} from '../../../display/tables/InputRadioDefaultTable/types';
import { tableIds } from '../../../display/tables/types';
import { dataTypes as DataTypesGenericOrderModal } from '../../../display/tables/InputRadioDefaultTable/types';
import { dataTypes } from '../../../display/tables/CampaignTable/types';
import { formNaming } from '../../../forms/NewCommunicationForm/constants';

// Components
import ModalContainer from '../../containers/ModalContainer';
import InputRadioDefaultTable from '../../../display/tables/InputRadioDefaultTable';
import { useDispatch } from 'react-redux';
import { closeModal } from '../../../../state/actions/ui/modal';
import { RelatedCampaignTable } from '../../../display/tables/RelatedCampaignTable';

// GraphQL
import {
  FETCH_CAPITOLS,
  FETCH_SUB_CAPITOLS,
  FETCH_CLASSIFICATIONS,
  FETCH_PREDEFINED_ORDERS,
} from '../../../../gql/queries/orders/predefinedData';
import { FETCH_TEAMS } from '../../../../gql/queries/users';
import { GET_CONCESSIONS } from '../../../../gql/queries/orders/orders';
import { client } from '../../../../gql/client';
import { GET_GENERIC_ORDERS_MODAL } from '../../../../gql/queries/genericOrders';
import { GET_CAMPAIGNS_MODAL } from '../../../../gql/queries/campaign';

// Selectors
// import { selectCommunicationOrdersWithStatusCapitolSubCapitolAndOnlyRoot } from '../../../../gql/selectors/communications';
import {
  selectedRowModalAcceptData,
  selectedRowModalTable,
} from '../../../../state/actions/ui/selectRowTableModal';

// Redux
import { getCursor } from '../../../../state/selectors/ui/tables';
import { selectAllOrders } from '../../../../gql/selectors/orders';
import { setCursor } from '../../../../state/actions/ui/cursorTables';
import { getCurrentIndexTabFormByTab } from '../../../../state/selectors/ui/tabs';
import { tabsType } from '../../../../state/reducers/ui/tabs';
import { campaignIdTypes } from '../../../../pages/main/workOrders/Campaigns/types';
import { loadingData } from '../../../../state/actions/ui/loadingData';
import * as Sentry from '@sentry/react';
import { recordCommunicationCreationPick } from '../../../../utils/communicationCreationDraft';
import { selectedOT } from '../../../../state/selectors/ui/modal';

export const RelatesCommunicationModal = ({
  onClickClose,
  namingPersistForm,
  ...props
}: ModalProps) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const dispatch = useDispatch();
  const { user } = useAuth();
  const roleActive = user?.user?.rolActive;
  const [genericOrdersData, setGenericOrdersData] = useState<dataTable[]>([]);
  const [campaignData, setCampaignData] = useState<dataTable[]>([]);
  const [campaignRowState, setCampaignRowState] = useState<campaignIdTypes>({});
  // True cuando el usuario ha seleccionado una fila en la tabla de OT genéricas.
  // Inicia en false (no hay nada seleccionado al abrir el modal). El callback que
  // pasamos a InputRadioDefaultTable (setIsSelectedRow) lo pone a true al seleccionar
  // y a false al deseleccionar. Antes este flag estaba invertido (init=true) y la
  // condición `disabled={genericOrderRowState}` mantenía el botón siempre deshabilitado.
  const [hasSelectedGenericOrderRow, setHasSelectedGenericOrderRow] =
    useState<boolean>(false);

  const [allData, setAllData] = useState<any>();
  const [currentServerPage, setCurrentServerPage] = useState(1);
  const skipTakeSyncRef = useRef(false);

  const selectorCursorGenericOrders = useSelector(
    getCursor(tableIds.GENERIC_ORDER_MODAL),
    isEqual
  );

  const selectorCursorCampaign = useSelector(
    getCursor(tableIds.CAMPAIGN_MODAL),
    isEqual
  );

  const [pageSizeCampaigns, setPageSizeCampaigns] = useState(
    selectorCursorCampaign.take
  );

  const [pageSizeGenericsOrders, setPageSizeGenericsOrders] = useState(
    selectorCursorGenericOrders.take
  );

  const selectorTabType = () => {
    switch (namingPersistForm) {
      case formNaming.NEW_COMMUNICATION_FORM:
        return tabsType.COMMUNICATION;

      case formNaming.NEW_DUPLICATE_COMMUNICATION_FORM:
        return tabsType.DUPLICATE_COMMUNICATION;

      case formNaming.EDIT_COMMUNICATION_FORM:
        return tabsType.EDIT_COMMUNICATION;
    }
  };

  const selectorIndexTab = useSelector(
    getCurrentIndexTabFormByTab(selectorTabType() as string),
    isEqual
  );

  // Determine if this modal should show Generic Orders or Campaigns based on namingPersistForm
  const isShowingCampaigns =
    namingPersistForm === formNaming.NEW_COMMUNICATION_FORM_CAMPAIGN ||
    namingPersistForm === formNaming.EDIT_COMMUNICATION_FORM_CAMPAIGN ||
    namingPersistForm === formNaming.NEW_DUPLICATE_COMMUNICATION_FORM_CAMPAIGN;

  const isShowingGenericOrders =
    namingPersistForm === formNaming.NEW_COMMUNICATION_FORM_OT ||
    namingPersistForm === formNaming.EDIT_COMMUNICATION_FORM_OT ||
    namingPersistForm === formNaming.NEW_DUPLICATE_COMMUNICATION_FORM_OT;

  // Reset a página 1 al abrir la modal
  useEffect(() => {
    setCurrentServerPage(1);
    dispatch(setCursor(tableIds.GENERIC_ORDER_MODAL as string, {
      ...selectorCursorGenericOrders,
      cursor: '',
      take: Math.abs(selectorCursorGenericOrders.take) || 8,
    }));
    dispatch(setCursor(tableIds.CAMPAIGN_MODAL as string, {
      ...selectorCursorCampaign,
      cursor: '',
      take: Math.abs(selectorCursorCampaign.take) || 8,
    }));
  }, []); // Solo al montar

  // FIX A4: Pre-set concessionId si solo hay 1 concesión (evita partición de cache Apollo)
  const autoConcessionId = (() => {
    try {
      const cached = client.readQuery({ query: GET_CONCESSIONS });
      if (cached?.concessions?.length === 1) return cached.concessions[0].id;
    } catch { /* cache not available */ }
    return undefined;
  })();
  const effectiveGenericConcessionId = selectorCursorGenericOrders.concessionId || autoConcessionId;
  const effectiveCampaignConcessionId = selectorCursorCampaign?.concessionId || autoConcessionId;

  useEffect(() => {
    // FIX A4: Sincronizar auto-concesión al Redux cursor si no está seteada
    if (autoConcessionId) {
      if (!selectorCursorGenericOrders.concessionId) {
        dispatch(setCursor(tableIds.GENERIC_ORDER_MODAL as string, { ...selectorCursorGenericOrders, concessionId: autoConcessionId }));
      }
      if (!selectorCursorCampaign?.concessionId) {
        dispatch(setCursor(tableIds.CAMPAIGN_MODAL as string, { ...selectorCursorCampaign, concessionId: autoConcessionId }));
      }
    }
  }, [autoConcessionId]);

  useEffect(() => {
    if (skipTakeSyncRef.current) {
      console.log(`%c[MODAL-SYNC] SKIPPED take sync (skipTakeSyncRef=true) | redux take: generic=${selectorCursorGenericOrders.take} campaign=${selectorCursorCampaign.take}`, 'color: #ff9900');
      skipTakeSyncRef.current = false;
      return;
    }
    console.log(`%c[MODAL-SYNC] Syncing pageSize from Redux | generic=${selectorCursorGenericOrders.take}→${Math.abs(selectorCursorGenericOrders.take)} campaign=${selectorCursorCampaign.take}→${Math.abs(selectorCursorCampaign.take)}`, 'color: #ff9900; font-weight: bold');
    setPageSizeCampaigns(Math.abs(selectorCursorCampaign.take));
    setPageSizeGenericsOrders(Math.abs(selectorCursorGenericOrders.take));
  }, [selectorCursorCampaign, selectorCursorGenericOrders]);

  // Reset page counter when filters change (cursor resets to '')
  useEffect(() => {
    if (selectorCursorGenericOrders.cursor === '') {
      setCurrentServerPage(1);
    }
  }, [selectorCursorGenericOrders.cursor]);

  useEffect(() => {
    if (selectorCursorCampaign?.cursor === '') {
      setCurrentServerPage(1);
    }
  }, [selectorCursorCampaign?.cursor]);

  const { error, loading, data } = isShowingCampaigns
    ? useQuery(GET_CAMPAIGNS_MODAL, {
        fetchPolicy: 'cache-first',
        variables: {
          input: {
            cursor: selectorCursorCampaign?.cursor,
            take: selectorCursorCampaign?.take,
            orderBy: selectorCursorCampaign?.orderBy,
            concessionId: effectiveCampaignConcessionId,
            fromDate: selectorCursorCampaign?.fromDate,
            toDate: selectorCursorCampaign?.toDate,
            name: selectorCursorCampaign.name,
            state: selectorCursorCampaign.state,
            teamIds: selectorCursorCampaign?.teamIds ?? [],
            track: selectorCursorCampaign?.track,
            predefinedOrderId: selectorCursorCampaign?.predefinedOrderId,
            pkInitKm: selectorCursorCampaign?.pkInitKm,
            pkInitMeter: selectorCursorCampaign?.pkInitMeter,
            pkEndKm: selectorCursorCampaign?.pkEndKm,
            pkEndMeter: selectorCursorCampaign?.pkEndMeter,
          },
        },
      })
    : useQuery(GET_GENERIC_ORDERS_MODAL, {
        fetchPolicy: 'cache-first',
        variables: {
          input: {
            take: selectorCursorGenericOrders.take,
            orderBy: selectorCursorGenericOrders.orderBy,
            cursor: selectorCursorGenericOrders.cursor,
            toDate: selectorCursorGenericOrders.toDate,
            fromDate: selectorCursorGenericOrders.fromDate,
            indexType: selectorCursorGenericOrders.indexType,
            indexExpedition: selectorCursorGenericOrders.indexExpedition,
            classification: selectorCursorGenericOrders.classification,
            capitolId: selectorCursorGenericOrders.capitolId,
            subCapitolId: selectorCursorGenericOrders.subCapitolId,
            concessionId: effectiveGenericConcessionId,
            pkInitKm: selectorCursorGenericOrders.pkInitKm,
            pkInitMeter: selectorCursorGenericOrders.pkInitMeter,
            pkEndKm: selectorCursorGenericOrders.pkEndKm,
            pkEndMeter: selectorCursorGenericOrders.pkEndMeter,
            teamIds: selectorCursorGenericOrders.teamIds ?? [],
            track: selectorCursorGenericOrders.track,
            predefinedOrderId: selectorCursorGenericOrders.predefinedOrderId,
            state: selectorCursorGenericOrders.state,
          },
        },
      });

  // El último `data` de la consulta que se copió a `allData`. Entre que llega
  // y el efecto lo copia hay un render en el que las filas siguen siendo las
  // de antes: también cuenta como cargando.
  const [syncedQueryData, setSyncedQueryData] = useState<unknown>();
  // Para qué `allData` están construidas las filas que se ven. Las filas no
  // salen de la consulta: las construye `selectAllOrders` DESPUÉS, con sus
  // consultas por fila, y mientras tanto se ven las de la página anterior.
  const [rowsBuiltFor, setRowsBuiltFor] = useState<unknown>();

  // Cargando de punta a punta (feedback usuario 28/10): la consulta Y las
  // filas construidas para lo último que llegó. Con solo `loading` el cargando
  // se apagaba con las filas viejas a la vista y parecía que el clic de página
  // no había hecho nada.
  const isBuildingRows =
    (!loading && data !== undefined && data !== syncedQueryData) ||
    rowsBuiltFor !== allData;
  const tableLoading = loading || isBuildingRows;

  useEffect(() => {
    isShowingCampaigns
      ? dispatch(loadingData(tableIds.CAMPAIGN_MODAL, tableLoading))
      : dispatch(loadingData(tableIds.GENERIC_ORDER_MODAL, tableLoading));
  }, [tableLoading, isShowingCampaigns]);

  // FIX (loader-loading-flag-leak): cleanup en unmount como defense-in-depth.
  // Si el usuario cierra el modal con la X mientras `loading` es true, sin
  // este cleanup el flag queda en true en Redux. En la práctica el siguiente
  // modal lo arregla solo (su useEffect dispatcha en el primer render con
  // el `loading` actual de useQuery, sobrescribiendo el flag fugado), y
  // GENERIC_ORDER_MODAL/CAMPAIGN_MODAL solo los leen las tablas internas
  // de los propios modales — pero mantenemos el cleanup para evitar la
  // ventana de flicker (cache hit instantáneo) y por coherencia con el
  // patrón "todo productor de loading flag debe limpiar".
  useEffect(() => {
    return () => {
      dispatch(loadingData(tableIds.CAMPAIGN_MODAL, false));
      dispatch(loadingData(tableIds.GENERIC_ORDER_MODAL, false));
    };
  }, []);

  useEffect(() => {
    if (!loading && data) {
      setAllData(data);
      setSyncedQueryData(data);
    }
  }, [data, loading]);

  const { refetch } = isShowingCampaigns
    ? useQuery(GET_CAMPAIGNS_MODAL, {
        skip: true,
        variables: {
          input: {
            cursor: '',
            take: Math.abs(pageSizeCampaigns),
            orderBy: 'desc',
            concessionId: effectiveCampaignConcessionId,
            fromDate: selectorCursorCampaign?.fromDate,
            toDate: selectorCursorCampaign?.toDate,
            name: selectorCursorCampaign.name,
            state: selectorCursorCampaign.state,
            teamIds: selectorCursorCampaign?.teamIds ?? [],
            track: selectorCursorCampaign?.track,
            predefinedOrderId: selectorCursorCampaign?.predefinedOrderId,
            pkInitKm: selectorCursorCampaign?.pkInitKm,
            pkInitMeter: selectorCursorCampaign?.pkInitMeter,
            pkEndKm: selectorCursorCampaign?.pkEndKm,
            pkEndMeter: selectorCursorCampaign?.pkEndMeter,
          },
        },
      })
    : useQuery(GET_GENERIC_ORDERS_MODAL, {
        skip: true,
        variables: {
          input: {
            take: Math.abs(pageSizeGenericsOrders),
            orderBy: 'desc',
            cursor: '',
            toDate: selectorCursorGenericOrders.toDate,
            fromDate: selectorCursorGenericOrders.fromDate,
            indexType: selectorCursorGenericOrders.indexType,
            indexExpedition: selectorCursorGenericOrders.indexExpedition,
            classification: selectorCursorGenericOrders.classification,
            capitolId: selectorCursorGenericOrders.capitolId,
            subCapitolId: selectorCursorGenericOrders.subCapitolId,
            concessionId: effectiveGenericConcessionId,
            pkInitKm: selectorCursorGenericOrders.pkInitKm,
            pkInitMeter: selectorCursorGenericOrders.pkInitMeter,
            pkEndKm: selectorCursorGenericOrders.pkEndKm,
            pkEndMeter: selectorCursorGenericOrders.pkEndMeter,
            teamIds: selectorCursorGenericOrders.teamIds ?? [],
            track: selectorCursorGenericOrders.track,
            predefinedOrderId: selectorCursorGenericOrders.predefinedOrderId,
            state: selectorCursorGenericOrders.state,
          },
        },
      });

  // We take methods exposed in InputRadioDefaultTable with useImperativeHandle

  const ref = useRef<imperativeRefPagination>(null);

  const reduxStore = useStore();
  const acceptData = useCallback(() => {
    dispatch(closeModal());
    dispatch(selectedRowModalAcceptData(namingPersistForm));
    // Elegida A MANO para esta alta: se apunta en la pestaña para que, al
    // recargar, no se confunda con la selección de OTRA pestaña que trae
    // redux-persist (revisión de la Task 12a). Solo para nueva comunicación.
    // Se lee de Redux, que es lo que usa el guardado: la tabla de OT escribe
    // ahí su fila y la de campañas llega por `campaignRowState`.
    const picked = selectedOT(namingPersistForm as string)(reduxStore.getState()) as
      | { id?: unknown }
      | undefined;
    recordCommunicationCreationPick(namingPersistForm, picked?.id);
  }, [namingPersistForm, reduxStore]);
  useEffect(() => {
    dispatch(
      selectedRowModalTable(namingPersistForm as string, campaignRowState)
    );
    // Una construcción vieja que acaba tarde no pisa las filas de otra más
    // nueva (otra página, otra búsqueda): al cambiar `allData` se cancela.
    let cancelled = false;
    const tasks = async () => {
      if (
        isShowingGenericOrders &&
        allData?.genericOrders?.genericOrders.length > 0
      ) {
        const genericOrders = await selectAllOrders(
          allData.genericOrders.genericOrders
        );
        if (cancelled) return;
        setGenericOrdersData(genericOrders);
      } else {
        setGenericOrdersData([]);
      }
      if (isShowingCampaigns && allData?.campaigns?.campaigns.length > 0) {
        const campaigns = await selectAllOrders(allData.campaigns.campaigns);
        if (cancelled) return;
        setCampaignData(campaigns);
      } else {
        setCampaignData([]);
      }
      setRowsBuiltFor(allData);
    };
    tasks().catch((err) => {
      Sentry.captureException(err);
      // Sin esto el cargando no se apagaría nunca. Quedan las filas de antes,
      // como hasta ahora.
      if (!cancelled) setRowsBuiltFor(allData);
    });
    return () => {
      cancelled = true;
    };
  }, [allData, error, campaignRowState]);

  const onReFetchData = useCallback(async () => {
    setCurrentServerPage(1);
    const { data } = await refetch();
    setAllData(data);
  }, [refetch]);

  const onReFetchPagination = useCallback(async () => {
    return data;
  }, [data]);

  const [dataCapitols, dataConcessions, dataSubCapitols, dataTeams, dataClassifications, dataPredefinedOrders] = [
    useQuery(FETCH_CAPITOLS),
    useQuery(GET_CONCESSIONS),
    useQuery(FETCH_SUB_CAPITOLS),
    useQuery(FETCH_TEAMS),
    useQuery(FETCH_CLASSIFICATIONS),
    useQuery(FETCH_PREDEFINED_ORDERS),
  ];
  const filters = {
    concessions: dataConcessions?.data?.concessions
      ? dataConcessions.data.concessions
      : [],
    teams: dataTeams?.data?.teams ? dataTeams.data.teams : [],
    predefinedOrders: dataPredefinedOrders?.data?.predefinedOrders
      ? dataPredefinedOrders.data.predefinedOrders
      : [],
  };
  const filtersDataOptions = {
    capitols: dataCapitols?.data?.capitols ? dataCapitols.data.capitols : [],
    concessions: dataConcessions?.data?.concessions
      ? dataConcessions.data.concessions
      : [],
    subCapitols: dataSubCapitols?.data?.subCapitols
      ? dataSubCapitols.data.subCapitols
      : [],
    teams: dataTeams?.data?.teams ? dataTeams.data.teams : [],
    classifications: dataClassifications?.data?.classifications
      ? dataClassifications.data.classifications
      : [],
    predefinedOrders: dataPredefinedOrders?.data?.predefinedOrders
      ? dataPredefinedOrders.data.predefinedOrders
      : [],
  };

  const rawPageInfo = isShowingCampaigns
    ? data?.campaigns?.pageInfo
    : data?.genericOrders?.pageInfo;

  const rawTotalCount = isShowingCampaigns
    ? data?.campaigns?.totalCount
    : data?.genericOrders?.totalCount;

  const pageInfo = rawPageInfo;
  const totalCount = rawTotalCount;

  // `loading` de las tablas = el de punta a punta (RelatedCampaignTable lo lee
  // de aquí; InputRadioDefaultTable, del flag de Redux de arriba).
  const combinedData = isShowingCampaigns
    ? {
        campaigns: campaignData,
        filters,
        totalCount,
        pageInfo,
        loading: tableLoading,
        roleActive,
      }
    : {
        genericOrdersData,
        filtersDataOptions,
        totalCount,
        pageInfo,
        loading: tableLoading,
        roleActive,
      };

  const fetchGenericOrderID = useCallback(
    async (id: string, take = pageSizeGenericsOrders) => {
      const dataSelector = {
        take,
        cursor: id,
        indexType: selectorCursorGenericOrders?.indexType,
        indexExpedition: selectorCursorGenericOrders?.indexExpedition,
        concessionId: effectiveGenericConcessionId,
        fromDate: selectorCursorGenericOrders?.fromDate,
        toDate: selectorCursorGenericOrders?.toDate,
        orderBy: 'desc',
        classification: selectorCursorGenericOrders?.classification,
        capitolId: selectorCursorGenericOrders?.capitolId,
        subCapitolId: selectorCursorGenericOrders?.subCapitolId,
        pkInitKm: selectorCursorGenericOrders?.pkInitKm,
        pkInitMeter: selectorCursorGenericOrders?.pkInitMeter,
        pkEndKm: selectorCursorGenericOrders?.pkEndKm,
        pkEndMeter: selectorCursorGenericOrders?.pkEndMeter,
        teamIds: selectorCursorGenericOrders?.teamIds ?? [],
        track: selectorCursorGenericOrders?.track,
        predefinedOrderId: selectorCursorGenericOrders?.predefinedOrderId,
        state: selectorCursorGenericOrders?.state,
      };

      if (take > 0) {
        const lastGenericOrder =
          genericOrdersData[genericOrdersData.length - 1]
            .genericOrCampaignOrderID;
        if (lastGenericOrder) {
          dispatch(
            setCursor(tableIds.GENERIC_ORDER_MODAL as string, dataSelector)
          );
        }
      } else {
        const firstGenericOrder = genericOrdersData[0].genericOrCampaignOrderID;

        if (firstGenericOrder) {
          dispatch(
            setCursor(tableIds.GENERIC_ORDER_MODAL as string, dataSelector)
          );
        }
      }

      return dataSelector;
    },
    [selectorCursorGenericOrders, genericOrdersData]
  );

  const fetchCampaignID = useCallback(
    async (id: string, take = pageSizeCampaigns) => {
      const dataSelector = {
        take,
        cursor: id,
        concessionId: effectiveCampaignConcessionId,
        fromDate: selectorCursorCampaign?.fromDate,
        toDate: selectorCursorCampaign?.toDate,
        orderBy: 'desc',
        state: selectorCursorCampaign?.state,
        name: selectorCursorCampaign?.name,
        teamIds: selectorCursorCampaign?.teamIds ?? [],
        track: selectorCursorCampaign?.track,
        predefinedOrderId: selectorCursorCampaign?.predefinedOrderId,
        pkInitKm: selectorCursorCampaign?.pkInitKm,
        pkInitMeter: selectorCursorCampaign?.pkInitMeter,
        pkEndKm: selectorCursorCampaign?.pkEndKm,
        pkEndMeter: selectorCursorCampaign?.pkEndMeter,
      };

      if (take > 0) {
        const lastCampaign =
          campaignData[campaignData.length - 1].genericOrCampaignOrderID;
        if (lastCampaign) {
          dispatch(setCursor(tableIds.CAMPAIGN_MODAL as string, dataSelector));
        }
      } else {
        const firstCampaign = campaignData[0].genericOrCampaignOrderID;

        if (firstCampaign) {
          dispatch(setCursor(tableIds.CAMPAIGN_MODAL as string, dataSelector));
        }
      }

      return dataSelector;
    },
    [selectorCursorCampaign, campaignData]
  );

  const nextPagination = async () => {
    console.log(`%c[MODAL-NEXT] page=${currentServerPage} pageSizeGeneric=${pageSizeGenericsOrders} pageSizeCampaign=${pageSizeCampaigns} dataLen=${isShowingCampaigns ? campaignData.length : genericOrdersData.length} hasNext=${pageInfo?.hasNextPage}`, 'color: #00cc00; font-weight: bold');
    if (
      isShowingGenericOrders &&
      genericOrdersData.length >= 1 &&
      pageInfo.hasNextPage
    ) {
      setCurrentServerPage((prev) => prev + 1);
      await fetchGenericOrderID(
        genericOrdersData[genericOrdersData.length - 1]
          .genericOrCampaignOrderID as string
      );
      onReFetchPagination();
    }
    if (
      isShowingCampaigns &&
      campaignData.length >= 1 &&
      pageInfo.hasNextPage
    ) {
      setCurrentServerPage((prev) => prev + 1);
      await fetchCampaignID(
        campaignData[campaignData.length - 1]
          .genericOrCampaignOrderID as string,
        pageSizeCampaigns
      );
      onReFetchPagination();
    }
  };

  const backPagination = async () => {
    // If going to page 1, reset cursor to get correct first items
    if (currentServerPage <= 2) {
      const dataCursor = isShowingCampaigns
        ? {
            take: pageSizeCampaigns,
            cursor: '',
            concessionId: effectiveCampaignConcessionId,
            fromDate: selectorCursorCampaign?.fromDate,
            toDate: selectorCursorCampaign?.toDate,
            orderBy: 'desc',
            name: selectorCursorCampaign?.name,
            state: selectorCursorCampaign?.state,
            teamIds: selectorCursorCampaign?.teamIds ?? [],
            track: selectorCursorCampaign?.track,
            predefinedOrderId: selectorCursorCampaign?.predefinedOrderId,
            pkInitKm: selectorCursorCampaign?.pkInitKm,
            pkInitMeter: selectorCursorCampaign?.pkInitMeter,
            pkEndKm: selectorCursorCampaign?.pkEndKm,
            pkEndMeter: selectorCursorCampaign?.pkEndMeter,
          }
        : {
            take: pageSizeGenericsOrders,
            cursor: '',
            indexType: selectorCursorGenericOrders?.indexType,
            indexExpedition: selectorCursorGenericOrders?.indexExpedition,
            concessionId: effectiveGenericConcessionId,
            fromDate: selectorCursorGenericOrders?.fromDate,
            toDate: selectorCursorGenericOrders?.toDate,
            classification: selectorCursorGenericOrders?.classification,
            orderBy: 'desc',
            capitolId: selectorCursorGenericOrders?.capitolId,
            subCapitolId: selectorCursorGenericOrders?.subCapitolId,
            state: selectorCursorGenericOrders?.state,
            pkInitKm: selectorCursorGenericOrders?.pkInitKm,
            pkInitMeter: selectorCursorGenericOrders?.pkInitMeter,
            pkEndKm: selectorCursorGenericOrders?.pkEndKm,
            pkEndMeter: selectorCursorGenericOrders?.pkEndMeter,
            teamIds: selectorCursorGenericOrders?.teamIds ?? [],
            track: selectorCursorGenericOrders?.track,
            predefinedOrderId: selectorCursorGenericOrders?.predefinedOrderId,
          };
      setCurrentServerPage(1);
      isShowingCampaigns
        ? dispatch(setCursor(tableIds.CAMPAIGN_MODAL as string, dataCursor))
        : dispatch(setCursor(tableIds.GENERIC_ORDER_MODAL as string, dataCursor));
      onReFetchPagination();
      return;
    }
    if (
      isShowingGenericOrders &&
      genericOrdersData.length >= 1 &&
      pageInfo.hasPreviousPage
    ) {
      setCurrentServerPage((prev) => Math.max(1, prev - 1));
      await fetchGenericOrderID(
        genericOrdersData[0].genericOrCampaignOrderID as string,
        -Math.abs(pageSizeGenericsOrders)
      );
      onReFetchPagination();
    }
    if (
      isShowingCampaigns &&
      campaignData.length >= 1 &&
      pageInfo.hasPreviousPage
    ) {
      setCurrentServerPage((prev) => Math.max(1, prev - 1));
      await fetchCampaignID(
        campaignData[0].genericOrCampaignOrderID as string,
        -Math.abs(pageSizeCampaigns)
      );
      onReFetchPagination();
    }
  };

  return (
    <ModalContainer onClickClose={onClickClose} fill={theme.colors.greyBoard}>
      <Container {...props}>
        {isShowingCampaigns ? (
          <RelatedCampaignTable
            titleText={t('pages:campaigns.filterBy')}
            data={combinedData as dataTypes}
            setRowState={setCampaignRowState}
            onReFetchData={onReFetchData}
            onReFetchPagination={onReFetchPagination}
            tableId={tableIds.CAMPAIGN_MODAL}
            showButtonsPagination={false}
            setPageSize={setPageSizeCampaigns}
            onDoubleTapAccept={acceptData}
            namingPersistForm={namingPersistForm}
            disabledRowStates={['end', 'annulled']}
          />
        ) : (
          <InputRadioDefaultTable
            data={combinedData as DataTypesGenericOrderModal}
            onReFetchData={onReFetchData}
            onRefetchPagination={onReFetchPagination}
            namingPersistForm={namingPersistForm}
            ref={ref}
            titleText={t('pages:workOrders.filterBy')}
            tableId={tableIds.GENERIC_ORDER_MODAL}
            setIsSelectedRow={setHasSelectedGenericOrderRow}
            onDoubleTapAccept={acceptData}
            disabledRowStates={['end', 'annulled']}
          />
        )}
        <ButtonContainer>
          <CloseButton
            text={t('common:buttons.back')}
            variant="secondary"
            onClick={onClickClose}
          />
          <PaginationButtonsStyled
            pageCount={Math.max(1, Math.ceil((totalCount ?? 0) / Math.abs(isShowingCampaigns ? pageSizeCampaigns : pageSizeGenericsOrders)))}
            currentPage={currentServerPage}
            nextPage={() => pageInfo?.hasNextPage && currentServerPage < Math.max(1, Math.ceil((totalCount ?? 0) / Math.abs(isShowingCampaigns ? pageSizeCampaigns : pageSizeGenericsOrders))) && nextPagination()}
            previousPage={() => currentServerPage > 1 && backPagination()}
            totalCount={totalCount}
            // Mientras carga, las filas a la vista son las de antes y el cursor
            // de la página siguiente sale de su última fila: pediría otra vez
            // la misma página.
            isDisabled={tableLoading}
          />
          <MainButtonStyled
            disabled={
              isShowingCampaigns
                ? !campaignRowState?.id
                : !hasSelectedGenericOrderRow
            }
            text={t('modals:relatesCommunication.relateButton')}
            onClick={acceptData}
          />
        </ButtonContainer>
      </Container>
    </ModalContainer>
  );
};
